import type {
  UpdateUserConfigurationMutation,
  UpdateUserConfigurationMutationVariables,
  UserConfigurationQuery,
  UserConfigurationQueryVariables,
} from '@api/dto/gql-helpers.generated';
import {
  UpdateUserConfigurationDocument,
  UserConfigurationDocument,
} from '@api/dto/gql-helpers.generated';
import { createEffect, createEvent, createStore, sample } from 'effector';

import { STORAGE_KEYS } from '@constants/app';

import { apolloClient } from '@system/apolloClient';

import type { ThemeName, ThemeValue } from './types';
import { getTheme } from './utils/getTheme';

type UserConfiguration = {
  themeName?: ThemeName;
} & Record<string, unknown>;

type ThemeStore = {
  theme: ThemeValue | null;
  loading: boolean;
};

export const $themeStore = createStore<ThemeStore>({
  theme: null,
  loading: false,
});

const getSettings = async (): Promise<UserConfiguration> => {
  const response = await apolloClient.query<
    UserConfigurationQuery,
    UserConfigurationQueryVariables
  >({
    query: UserConfigurationDocument,
    fetchPolicy: 'cache-first',
  });

  const data = response.data.configuration.user_configuration?.data;
  const parsedConfig = (data ? JSON.parse(data) : {}) as UserConfiguration;
  return parsedConfig;
};

export const initData = createEvent<boolean>();
const initDataFx = createEffect({
  handler: async (useNetwork: boolean): Promise<ThemeValue> => {
    let defaultThemeName: ThemeName | null =
      (localStorage.getItem(STORAGE_KEYS.THEME_NAME) as ThemeName) ?? null;

    if (!defaultThemeName && useNetwork) {
      const parsedConfig = await getSettings();
      defaultThemeName = parsedConfig.themeName as ThemeName | null;
      localStorage.setItem(
        STORAGE_KEYS.THEME_NAME,
        defaultThemeName ?? 'Light',
      );
    }

    const defaultTheme = await getTheme(defaultThemeName)();

    return defaultTheme;
  },
});

export const setData = createEvent<ThemeName>();
const setDataFx = createEffect({
  handler: async (themeName: ThemeName): Promise<ThemeValue> => {
    const userConfiguration = await getSettings();
    const params = {
      variables: {
        data: JSON.stringify({ ...userConfiguration, themeName }),
      },
    };
    await apolloClient.mutate<
      UpdateUserConfigurationMutation,
      UpdateUserConfigurationMutationVariables
    >({
      ...params,
      mutation: UpdateUserConfigurationDocument,
    });

    const newTheme = await getTheme(themeName)();
    localStorage.setItem(STORAGE_KEYS.THEME_NAME, themeName);

    return newTheme;
  },
});

$themeStore
  .on(initDataFx.pending, (state, payload) => {
    if (payload !== state.loading) {
      return {
        ...state,
        loading: payload,
      };
    } else {
      return state;
    }
  })
  .on(initDataFx.done, (state, { result }) => {
    return {
      ...state,
      theme: result,
      loading: false,
    };
  })
  .on(setDataFx.done, (state, payload) => ({
    ...state,
    theme: payload.result,
  }));

sample({
  clock: setData,
  filter: initDataFx.pending.map(pending => !pending),
  target: setDataFx,
});

sample({
  clock: initData,
  target: initDataFx,
});
